import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react'
import { useAppState } from '../../../utils/hooks'
import Utils from '../../../utils/utils'
import { AccountTradingTypes } from '../../../utils/enums'
import {
    AppropriatenessResult,
    OnboardingBaseInput,
    OnboardingEventStep,
    buildAppropriatenessResultEvent,
    buildKycSubmittedEvent,
    buildLeadEvent,
    buildOnboardingStepEvent,
    buildOnboardingSubmittedEvent,
    buildPhoneVerifiedEvent,
    buildPlatformSelectedEvent,
    buildTaxIdDeferralEvent,
    TaxIdDeferralEventName,
    buildProspectEvent,
} from '../../../utils/floodlightEvents'

// Shared across every useOnboardingAnalytics() consumer under the same onboarding
// mount so the appropriateness verdict fired mid-flow (GeneralFlow) is not fired
// again at completion (completeGeneral). A fresh mount (a retake) resets it, so
// retakes intentionally re-fire.
type ResultDedupe = React.MutableRefObject<AppropriatenessResult | null>
const OnboardingAnalyticsContext = createContext<ResultDedupe | null>(null)

export const OnboardingAnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const ref = useRef<AppropriatenessResult | null>(null)
    return <OnboardingAnalyticsContext.Provider value={ref}>{children}</OnboardingAnalyticsContext.Provider>
}

const toResult = (level?: string | null): AppropriatenessResult | null => {
    switch ((level || '').toUpperCase()) {
        case 'PASS': return 'pass'
        case 'REFER': return 'refer'
        case 'FAIL': return 'fail'
        default: return null
    }
}

const readSession = (key: string): string | undefined => {
    try { return sessionStorage.getItem(key) || undefined } catch { return undefined }
}

export const useOnboardingAnalytics = (simplified = false) => {
    const {
        userProfile: user,
        uncompletedApplication: app,
        onboardingCountry,
        onboardingDomain,
        portalAccountDomain,
        language,
        loggedIn,
        applicationToCreate,
        liveAccounts,
        countries,
    } = useAppState()

    const ctxRef = useContext(OnboardingAnalyticsContext)
    const localRef = useRef<AppropriatenessResult | null>(null)
    const resultDedupe = ctxRef ?? localRef

    // Retail only: never fire for demo accounts or IB/partner applications.
    const enabled = useMemo(() => {
        const appType = app?.accountType
        const isDemo = appType === 'demo' || applicationToCreate === 'demo'
        const isPartner = appType === 'partner' || applicationToCreate === 'partner'
        return !isDemo && !isPartner
    }, [app?.accountType, applicationToCreate])

    const org = user?.organization?.name || onboardingDomain || portalAccountDomain || ''
    const country = onboardingCountry?.code2 || user?.country?.code2 || ''
    const countryName = onboardingCountry?.name || user?.country?.name || ''
    const userId = user?.id
    const nationality = user?.nationality?.name || countries?.find(c => c.id === app?.accountHolderNationality)?.name
    const currency = app?.accountCurrency || user?.country?.liveCurrency?.code
    // account_type is the account type the applicant selects (e.g. standard / thinkzero),
    // held on the application as platformAccountType; it falls back to the live account's
    // CE/CFD trading type once one exists, else na. tradingAccountToCreate is not used: it
    // is a never-dispatched "CFD" default that would otherwise pin every event to cfd.
    const accountType = app?.platformAccountType || liveAccounts?.[0]?.account?.accountTradingType?.accountTradingType
    const platform = app?.selectedPlatform
    const accountId = liveAccounts?.[0]?.account?.accountNumber
    const email = app?.accountHolderEmail

    const buildBase = useCallback((): OnboardingBaseInput => {
        // flow: a portal signup captures a password; a social signup stores its specific
        // provider (google|apple|facebook) at click. Password takes precedence so a portal
        // signup stays portal; otherwise the stored provider wins; otherwise default to
        // portal. Never infer 'social' from loggedIn: after account creation the password
        // leaves state, so every logged-in portal user would misread as social. Read fresh
        // at fire time.
        const storedProvider = readSession('tm_signup_provider')
        const flow = app?.accountHolderPassword ? 'portal' : (storedProvider || 'portal')
        // trading_type (cfd | ce): read from sessionStorage first because the AU
        // application_type event fires before submitApplication persists the choice;
        // fall back to the application state for every other flow (defaults to cfd).
        const tradingType = readSession('tm_onboarding_trading_type')
            || (Utils.retrieveAccTradingType(app?.accountTradingTypes) === AccountTradingTypes.ForexAndCFDs ? 'cfd' : 'ce')
        return {
            org: org || '',
            flow,
            country: country || '',
            countryName: countryName || '',
            language: language || '',
            loggedIn,
            referrer: document.referrer,
            portalHost: window.location.hostname,
            pathname: window.location.pathname,
            simplified: simplified || undefined,
            userId,
            nationality,
            currency,
            accountType,
            platform,
            accountId,
            email,
            selection: readSession('tm_onboarding_selection'),
            tradingType,
        }
    }, [org, app?.accountHolderPassword, app?.accountTradingTypes, country, countryName, language, loggedIn, simplified, userId, nationality, currency, accountType, platform, accountId, email])

    // Analytics must never break onboarding: swallow every build/push error.
    const safePush = useCallback((build: () => Record<string, string> | null) => {
        if (!enabled) return
        try {
            const payload = build()
            if (payload) window.dataLayer?.push(payload)
        } catch { /* noop */ }
    }, [enabled])

    const fireStepViewed = useCallback((step?: OnboardingEventStep) => {
        if (!step) return
        safePush(() => buildOnboardingStepEvent(step, 'viewed', buildBase()))
    }, [safePush, buildBase])

    const fireStepSubmitted = useCallback((step?: OnboardingEventStep) => {
        if (!step) return
        safePush(() => buildOnboardingStepEvent(step, 'submitted', buildBase()))
    }, [safePush, buildBase])

    const fireOtpRequested = useCallback((step?: OnboardingEventStep) => {
        if (!step) return
        safePush(() => buildOnboardingStepEvent(step, 'requested_otp', buildBase()))
    }, [safePush, buildBase])

    const fireOtpFailed = useCallback((step?: OnboardingEventStep) => {
        if (!step) return
        safePush(() => buildOnboardingStepEvent(step, 'otp_failed', buildBase()))
    }, [safePush, buildBase])

    const fireStepExited = useCallback((step?: OnboardingEventStep) => {
        if (!step) return
        safePush(() => buildOnboardingStepEvent(step, 'exited', buildBase()))
    }, [safePush, buildBase])

    const fireContactChange = useCallback((step: OnboardingEventStep) => {
        const action = step === 'phone' ? 'change_phone' : 'change_email'
        safePush(() => buildOnboardingStepEvent(step, action, buildBase()))
    }, [safePush, buildBase])

    const fireApplicationDenied = useCallback(() => {
        safePush(() => buildOnboardingStepEvent('submit', 'denied', buildBase()))
    }, [safePush, buildBase])

    const fireDeniedAction = useCallback((action: 'exit_to_dashboard' | 'contact_us') => {
        safePush(() => buildOnboardingStepEvent('submit', action, buildBase()))
    }, [safePush, buildBase])

    const fireIdVerification = useCallback((action: 'viewed' | 'submitted' | 'retry' | 'verify_later' | 'exit_to_dashboard') => {
        safePush(() => buildOnboardingStepEvent('id_verification', action, buildBase()))
    }, [safePush, buildBase])

    const firePlatformSelected = useCallback((platform?: string | null, accountType?: string | null) => {
        const base = buildBase()
        safePush(() => buildPlatformSelectedEvent(platform, accountType ? { ...base, accountType } : base))
    }, [safePush, buildBase])

    const fireTaxIdDeferralEvent = useCallback((name: TaxIdDeferralEventName) => {
        safePush(() => buildTaxIdDeferralEvent(name, buildBase()))
    }, [safePush, buildBase])

    const fireAppropriatenessResult = useCallback((level?: string | null) => {
        if (!enabled) return
        const result = toResult(level)
        if (!result || resultDedupe.current === result) return
        resultDedupe.current = result
        safePush(() => buildAppropriatenessResultEvent(result, buildBase()))
    }, [enabled, resultDedupe, safePush, buildBase])

    const fireOnboardingSubmitted = useCallback((opts?: { simplified?: boolean; level?: string; applicationId?: string | number | null }) => {
        const base = { ...buildBase(), simplified: opts?.simplified ?? simplified }
        // The submit folds into the onboarding funnel (step 'submit') and also fires
        // the distinct onboarding_submitted conversion event.
        safePush(() => buildOnboardingStepEvent('submit', 'submitted', base))
        safePush(() => buildOnboardingSubmittedEvent(base, opts?.level, opts?.applicationId))
    }, [safePush, buildBase, simplified])

    const fireProspect = useCallback(() => {
        safePush(() => buildProspectEvent(buildBase()))
    }, [safePush, buildBase])

    const fireLead = useCallback(() => {
        safePush(() => buildLeadEvent(buildBase()))
    }, [safePush, buildBase])

    const firePhoneVerified = useCallback(() => {
        safePush(() => buildPhoneVerifiedEvent(buildBase()))
    }, [safePush, buildBase])

    const fireKycSubmitted = useCallback((docType?: string | null) => {
        safePush(() => buildKycSubmittedEvent(docType, buildBase()))
    }, [safePush, buildBase])

    return {
        enabled,
        buildBase,
        fireStepViewed,
        fireStepSubmitted,
        fireOtpRequested,
        fireOtpFailed,
        fireStepExited,
        fireContactChange,
        fireApplicationDenied,
        fireDeniedAction,
        fireIdVerification,
        firePlatformSelected,
        fireTaxIdDeferralEvent,
        fireAppropriatenessResult,
        fireOnboardingSubmitted,
        fireProspect,
        fireLead,
        firePhoneVerified,
        fireKycSubmitted,
    }
}
